import { Box, useTheme } from '@mui/material';
import { type GridColDef, type GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  useAppDispatch,
  useAppSelector,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '@verantos-dev/core-annotation-tool';
import { DataGrid, DataGridLayout, Icon } from '@verantos-dev/shared-components';

import { useHolidaysDatagridFetch } from '../../hook/useHolidaysDataGridFetch copy';
import {
  setHolidaysTablePaginationModel,
  setIRRScoreTablePaginationModel,
} from '../../state/metricsSlice';
import { selectHolidaysTablePage, selectHolidaysTablePageSize } from '../../state/selector';
import { HolidaysDataItem } from '../../types/responseTypes';

export const HolidaysDataTable = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const holidaysTablePage = useAppSelector(selectHolidaysTablePage);
  const holidaysTablePageSize = useAppSelector(selectHolidaysTablePageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setHolidaysTablePaginationModel({ page: model.page, pageSize: model.pageSize }));
  };
  const { isFetching } = useHolidaysDatagridFetch() || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(setIRRScoreTablePaginationModel({ page: 0, pageSize: holidaysTablePageSize }));
    }
  }, [isFetching]);

  const columns: GridColDef<HolidaysDataItem>[] = useMemo(
    () => [
      {
        field: 'srNo',
        headerName: 'Sr No.',
        sortable: false,
        filterable: false,
        disableExport: true,
        minWidth: 50,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      {
        field: 'description',
        headerName: 'Description',
        minWidth: 150,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'date',
        headerName: 'Date',
        type: 'string',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const value = params.row.date;

          if (!value) return '-';

          const parsed = dayjs(value);

          return parsed.isValid() ? parsed.format('MMM D, YYYY') : `${value}`;
        },
      },
    ],
    [formatMessage, theme]
  );

  return (
    <Box>
      <DataGridLayout id="metrics-holidays-data-grid">
        <DataGrid
          id="metrics-holidays-data-grid"
          mode="client"
          pagination
          loadOnFetch
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          paginationModel={{ page: holidaysTablePage, pageSize: holidaysTablePageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSearchParamSync
          preventUrlReadWhenSyncDisabled
          getRowId={(row) => row.id}
          columns={columns}
          useFetchDataGridData={useHolidaysDatagridFetch}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
